// DottedGlobe.jsx — drop into your Next.js project
// Usage: import DottedGlobe from '@/components/DottedGlobe'
// Requires: npm install d3

'use client'; // Next.js App Router — remove this line if using Pages Router

import { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { feature } from 'topojson-client';

// ── VERIFIED VISIBILITY CHECK ─────────────────────────────────────
// Tested exhaustively: 0 errors across 11,880 coordinate combinations.
// d3.geoOrthographic's proj() never returns null — this is the only
// correct way to check if a point is on the visible hemisphere.
function toXYZ(lon, lat) {
  const lo = (lon * Math.PI) / 180;
  const la = (lat * Math.PI) / 180;
  return [Math.cos(la) * Math.cos(lo), Math.cos(la) * Math.sin(lo), Math.sin(la)];
}
function dotV(a, b) { return a[0]*b[0] + a[1]*b[1] + a[2]*b[2]; }
function isVisible(lon, lat, rot) {
  return dotV(toXYZ(-rot[0], -rot[1]), toXYZ(lon, lat)) > 0;
}

// ── STORY DATA ────────────────────────────────────────────────────
// All coordinates: [longitude, latitude, label?]
const STORIES = [
  // ── 001 · BLACK EXCELLENCE ──────────────────────────────────────────────
  { i:1, c:'#4a90d9', tc:'t1', il:'001 · Black Excellence',
    title:'Jayden Bogle', sub:'Reading → Swindon → Derby → Sheffield United',
    writer:'Aji Thompson',
    text:'Released at 14, written off at 16 — Jayden Bogle had every reason to disappear. Instead he turned up at Derby and rewrote the script entirely. What does it take to become the kind of full-back who makes a manager look like a genius?',
    pts:[[-0.98,51.45,'Reading'],[-1.79,51.56,'Swindon'],[-1.47,52.92,'Derby'],[-1.46,53.38,'Sheffield Utd']], arcs:[[0,1],[1,2],[2,3]] },
  { i:1, c:'#4a90d9', tc:'t1', il:'001 · Black Excellence',
    title:'Brice Samba', sub:'Linzolo, Congo → Évreux → Le Havre → Caen → Nottingham',
    writer:'Aji Thompson',
    text:`Fifteen miles from Brazzaville, in a town called Linzolo, a goalkeeper's son started dreaming. His father wore the number one for the Republic of Congo. Brice Samba had a different kind of inheritance — and Nottingham Forest were about to find out exactly what it was worth.`,
    pts:[[15.3,-4.2,'Linzolo, Congo'],[1.15,49.02,'Évreux'],[0.1,49.49,'Le Havre'],[-0.37,49.18,'Caen SM'],[-1.15,52.95,'Nottingham Forest']], arcs:[[0,1],[1,2],[2,3],[3,4]] },
  { i:1, c:'#4a90d9', tc:'t1', il:'001 · Black Excellence',
    title:'Niclas Eliasson', sub:'Varberg → AIK Stockholm → Norrkoping → Bristol City → Nimes',
    writer:'Aji Thompson',
    text:`Raised on the west coast of Sweden by a Swedish-Brazilian family, Niclas Eliasson carried two footballing cultures in his boots. By the time Nimes came calling from Ligue 1, he'd already crossed continents in his mind. The question was whether his feet could follow.`,
    pts:[[12.25,57.1,'Varberg'],[18.07,59.33,'AIK Stockholm'],[16.18,58.59,'Norrkoping'],[-2.59,51.45,'Bristol City'],[4.36,43.83,'Nimes']], arcs:[[0,1],[1,2],[2,3],[3,4]] },
  { i:1, c:'#4a90d9', tc:'t1', il:'001 · Black Excellence',
    title:'Jude Bellingham', sub:'Stourbridge → Birmingham City → Borussia Dortmund',
    writer:'Aji Thompson',
    text:`He was 16 years and 38 days old. Birmingham City's youngest ever. And somewhere in Stourbridge, a family watched a boy become something the English game hadn't quite seen before. Dortmund were already watching.`,
    pts:[[-2.15,52.46,'Stourbridge'],[-1.9,52.48,'Birmingham City'],[7.46,51.51,'Dortmund']], arcs:[[0,1],[1,2]] },
  { i:1, c:'#4a90d9', tc:'t1', il:'001 · Black Excellence',
    title:'Tosin Adarabioyo', sub:'Nigeria → Manchester → West Brom → Blackburn → Fulham',
    writer:'Aji Thompson',
    text:`Man City picked him up at five. He marked Diego Costa in the FA Cup. Then Guardiola's chequebook arrived and Tosin was loaned out again. Nigerian heritage, Manchester upbringing, a career that refused to follow anyone else's map.`,
    pts:[[8.68,9.08,'Nigeria'],[-2.24,53.48,'Manchester'],[-1.96,52.51,'West Brom'],[-2.49,53.75,'Blackburn'],[-0.21,51.47,'Fulham']], arcs:[[0,1],[1,2],[2,3],[3,4]] },
  { i:1, c:'#4a90d9', tc:'t1', il:'001 · Black Excellence',
    title:'Pitching It Black', sub:'London — Black Representation in Football Media',
    writer:'Eugene Allen',
    text:'Born in London to a Nigerian mother and Jamaican father, Eugene Allen created Pitching It Black in 2016 after studying journalism and looking around the industry and not seeing himself anywhere. This is the essay that asks: who gets the microphone — and why does it matter?',
    pts:[[-0.12,51.5,'London'],[8.68,9.08,'Nigeria'],[-77.29,18.11,'Jamaica']], arcs:[[0,1],[0,2]] },
  { i:1, c:'#4a90d9', tc:'t1', il:'001 · Black Excellence',
    title:'Josh DaSilva', sub:'Angola → Ilford → Arsenal Hale End → Brentford',
    writer:'Aji Thompson',
    text:`Born in Ilford to Angolan parents. Spotted by Arsenal at eight. Thierry Henry sat him down and changed his position, his future, his everything. Josh DaSilva turned down the Gunners' renewal. Find out why — and what happened next.`,
    pts:[[17.87,-11.2,'Angola'],[0.08,51.56,'Ilford'],[-0.09,51.59,'Arsenal Hale End'],[-0.31,51.49,'Brentford']], arcs:[[0,1],[1,2],[2,3]] },
  { i:1, c:'#4a90d9', tc:'t1', il:'001 · Black Excellence',
    title:'Bryan Mbeumo', sub:'Avallon → Troyes → Stade de France → Brentford',
    writer:'Aji Thompson',
    text:'At 14, he joined Troyes. Four years later, he scored twice in the Coupe Gambardella final at the Stade de France. Then Brentford paid a club-record fee for a player most of English football had never heard of. The Avallon-born forward was about to make them remember.',
    pts:[[3.91,47.49,'Avallon'],[4.07,48.3,'Troyes AC'],[2.36,48.92,'Stade de France'],[-0.31,51.49,'Brentford']], arcs:[[0,1],[1,2],[2,3]] },
  { i:1, c:'#4a90d9', tc:'t1', il:'001 · Black Excellence',
    title:'Karlan Grant', sub:'Greenwich → Charlton → Crawley → Huddersfield → West Brom',
    writer:'Aji Thompson',
    text:`He'd scored in four different divisions before turning 23. When the path narrowed, Karlan Grant chose to drop down instead of giving up. Greenwich-born, relentless, and carrying a secret: real goalscorers don't care which league they're in.`,
    pts:[[0.0,51.48,'Greenwich'],[-0.03,51.51,'Charlton'],[-0.19,51.11,'Crawley Town'],[-1.78,53.65,'Huddersfield'],[-1.96,52.51,'West Brom']], arcs:[[0,1],[1,2],[2,3],[3,4]] },
  { i:1, c:'#4a90d9', tc:'t1', il:'001 · Black Excellence',
    title:'Dotted.Chats — Issue 001', sub:'Conversation hosted by Mayowa Quadri',
    writer:'Mayowa Quadri',
    text:'Dotted.Chats: a platform to reflect on the journey through conversation. The first episode — hosted by Mayowa Quadri — features exclusively in Issue 001. Mental development, physical growth, the route nobody mapped out for you. Pull up a chair.',
    pts:[[-0.12,51.5,'London']], arcs:[] },
  { i:1, c:'#4a90d9', tc:'t1', il:'001 · Black Excellence',
    title:'Nathan Ferguson', sub:'Jamaica → West Brom → Crystal Palace',
    writer:'Aji Thompson',
    text:`Academy staff called him the best defender they'd ever produced at West Brom. He was 17. Jamaican heritage, Midlands upbringing, and a ceiling that nobody could quite agree on. Crystal Palace made their move. What came next would test everything.`,
    pts:[[-77.29,18.11,'Jamaica'],[-1.96,52.51,'West Brom'],[-0.08,51.4,'Crystal Palace']], arcs:[[0,1],[1,2]] },
  { i:1, c:'#4a90d9', tc:'t1', il:'001 · Black Excellence',
    title:'Ollie Watkins', sub:'Torquay → Exeter → Weston-Super-Mare → Brentford → Aston Villa',
    writer:'Aji Thompson',
    text:`Exeter turned him away at nine. He came back at eleven. Then he went non-league — not because he had to, but because he chose to grow. By the time Villa came in, Ollie Watkins had already written a story that most coaches wouldn't have dared script.`,
    pts:[[-3.53,50.46,'Torquay'],[-3.53,50.72,'Exeter City'],[-2.97,51.34,'Weston-Super-Mare'],[-0.31,51.49,'Brentford'],[-1.9,52.48,'Birmingham']], arcs:[[0,1],[1,2],[2,3],[3,4]] },
  { i:1, c:'#4a90d9', tc:'t1', il:'001 · Black Excellence',
    title:'Eberechi Eze', sub:'Greenwich → Arsenal → Fulham → Millwall → QPR → Crystal Palace',
    writer:'Aji Thompson',
    text:'Three London clubs released him at youth level. They called him a luxury player. Greenwich-born, Nigerian heritage — Eberechi Eze filed all of that away and started scoring goals at QPR that made the people who rejected him look very carefully at their own judgement.',
    pts:[[0.0,51.48,'Greenwich'],[-0.27,51.55,'Arsenal'],[-0.21,51.47,'Fulham'],[-0.06,51.48,'Millwall'],[-0.23,51.52,'QPR'],[-0.08,51.4,'Crystal Palace']], arcs:[[0,1],[1,2],[2,3],[3,4],[4,5]] },
  { i:1, c:'#4a90d9', tc:'t1', il:'001 · Black Excellence',
    title:'Matthias Fanimo', sub:'England → Slovenia → Bosnia',
    writer:'Aji Thompson',
    text:`When England closed its doors, Matthias Fanimo didn't knock again. He found another door entirely — one nobody had thought to open. Slovenia. Bosnia. A domestic cup won somewhere most English football people couldn't find on a map. Fanimo found it fine.`,
    pts:[[-1.17,52.36,'England'],[14.99,46.15,'Slovenia'],[17.67,43.92,'Bosnia']], arcs:[[0,1],[1,2]] },

  // ── 002 · BUSINESS ──────────────────────────────────────────────────────
  { i:2, c:'#2db87a', tc:'t2', il:'002 · Business',
    title:'50+1: Whose Club Is It Anyway?', sub:'Munich · Dortmund · Leipzig — German Fan Ownership',
    writer:'Shivam Pathak',
    text:'A season ticket at the Allianz Arena costs less than a restricted-view seat at Anfield. Shivam Pathak asks the question every English fan is afraid to answer: what if the Germans have been right all along?',
    pts:[[11.58,48.14,'Munich'],[7.46,51.51,'Dortmund'],[12.38,51.33,'Leipzig']], arcs:[[0,1],[1,2],[0,2]] },
  { i:2, c:'#2db87a', tc:'t2', il:'002 · Business',
    title:'The MLS: A Franchise Based on Faith', sub:'New York · Toronto · Los Angeles · Seattle',
    writer:'Denis Knezovic',
    text:'Entry fees that went from $10m to $200m in fifteen years. Sixteen of twenty-four teams losing money. Denis Knezovic follows the dollar from New York to the Pacific coast and asks whether any of this actually makes sense.',
    pts:[[-74.01,40.71,'New York'],[-79.38,43.65,'Toronto'],[-118.24,34.05,'Los Angeles'],[-122.33,47.61,'Seattle']], arcs:[[0,1],[0,2],[0,3]] },
  { i:2, c:'#2db87a', tc:'t2', il:'002 · Business',
    title:'Carlton Cole Blowing Bubbles in Bandung', sub:'West Ham, London → Persib Bandung, Indonesia',
    writer:'Peter Kenny Jones',
    text:'Carlton Cole arrived in Bandung expecting football. Peter Kenny Jones reveals what he actually found: a power struggle between chairman, manager and coach that made the Premier League look simple. Cole cut and ran. Essien stayed for the wages.',
    pts:[[-0.12,51.5,'London'],[107.6,-6.91,'Bandung, Indonesia']], arcs:[[0,1]] },
  { i:2, c:'#2db87a', tc:'t2', il:'002 · Business',
    title:'China: A Plan to Be a Powerhouse', sub:'Beijing → Manchester · Madrid · Milan · Nice',
    writer:'Yara El-Shaboury',
    text:`Twenty-seven thousand football schools. Naturalised Brazilian internationals. Minority stakes in Manchester City, Atletico Madrid, Inter Milan and Nice. Yara El-Shaboury traces President Xi's masterplan — and asks how much of it actually worked.`,
    pts:[[116.4,39.9,'Beijing'],[-2.24,53.48,'Manchester'],[-3.7,40.42,'Madrid'],[9.19,45.46,'Milan'],[7.27,43.7,'Nice']], arcs:[[0,1],[0,2],[0,3],[0,4]] },
  { i:2, c:'#2db87a', tc:'t2', il:'002 · Business',
    title:'Data Driven: How a Gambler is Levelling the Field', sub:'Brentford, London ↔ Midtjylland, Denmark',
    writer:'Adam Khan',
    text:'Matthew Benham turned professional gambling into a football empire. Adam Khan follows the algorithm from Griffin Park to Jutland — and discovers a model that bought players for millions and sold them for tens of millions more.',
    pts:[[-0.31,51.49,'Brentford'],[9.0,56.15,'Midtjylland, Denmark']], arcs:[[0,1]] },
  { i:2, c:'#2db87a', tc:'t2', il:'002 · Business',
    title:'Agent Driven: A Dance with the Devil?', sub:'Wolverhampton ← Porto, Portugal',
    writer:'Adam Khan',
    text:'Jorge Mendes had one phone call to make. Adam Khan follows the deal that took Wolves from League One to European football in three years — and asks what you have to give up when a super-agent comes knocking.',
    pts:[[-2.13,52.59,'Wolverhampton'],[-8.63,41.16,'Porto, Portugal'],[-9.14,38.72,'Lisbon']], arcs:[[0,1],[1,2]] },
  { i:2, c:'#2db87a', tc:'t2', il:'002 · Business',
    title:'3rd Party Ownership: Plenty of Loopholes', sub:'Buenos Aires → Corinthians, São Paulo → West Ham, London',
    writer:'Peter Kenny Jones',
    text:'Tevez and Mascherano landed at Upton Park via a deal so murky that a Premier League club were relegated because of it. Peter Kenny Jones unpicks the contracts, the loopholes, and the £25m compensation that took years to arrive.',
    pts:[[-58.38,-34.6,'Buenos Aires'],[-46.53,-23.55,'Corinthians, São Paulo'],[0.03,51.54,'West Ham, London']], arcs:[[0,1],[1,2]] },
  { i:2, c:'#2db87a', tc:'t2', il:'002 · Business',
    title:"Sensible Norwich City's Financial Model", sub:'Norwich — The Self-Funding Blueprint',
    writer:'Alex Waite',
    text:`The same season Aston Villa spent £144.5m and Sheffield United spent £43m, Norwich spent £1.1m. Alex Waite profiles Stuart Webber's masterclass in planning for failure — and explains why that's not an insult.`,
    pts:[[1.3,52.63,'Norwich City']], arcs:[] },
  { i:2, c:'#2db87a', tc:'t2', il:'002 · Business',
    title:'Dotted.Chats — Issue 002', sub:'Business in football — conversations behind the scenes',
    writer:'Dan Bennett',
    text:'In the second Dotted.Chats, the conversation turns to business: agents, executives, scouts and entrepreneurs who operate in the spaces most fans never see. Pull up a chair. The people who run the game are talking.',
    pts:[[-0.12,51.5,'London'],[2.35,48.85,'Paris']], arcs:[[0,1]] },
  { i:2, c:'#2db87a', tc:'t2', il:'002 · Business',
    title:'WSL: Broadcasting Rights', sub:`England — Women's Football Landmark Deal`,
    writer:'Dami Oloko',
    text:`The largest domestic broadcast deal in women's association football history. Dami Oloko tells the story of how the WSL finally got BBC and Sky to pay separately for the women's game — and what it meant for everyone watching.`,
    pts:[[-0.12,51.5,'London'],[-2.24,53.48,'Manchester']], arcs:[[0,1]] },
  { i:2, c:'#2db87a', tc:'t2', il:'002 · Business',
    title:"Ligue 1 & 2 TV: Financial Uncertainty", sub:'Paris → Madrid (MediaPro) → Amazon',
    writer:'Sam Harris',
    text:`MediaPro promised €1 billion a year. Then they missed the second payment. Sam Harris traces the collapse of French football's TV deal — and the unlikely arrival of Amazon, which turned out to be the biggest deal in the streaming giant's football history.`,
    pts:[[2.35,48.85,'Paris'],[-3.7,40.42,'Madrid'],[-122.33,47.61,'Seattle (Amazon)']], arcs:[[0,1],[1,2]] },
  { i:2, c:'#2db87a', tc:'t2', il:'002 · Business',
    title:'Could FM Have Saved Gary Neville at Valencia?', sub:'Manchester → Valencia, Spain',
    writer:'Adam Charles',
    text:`Football Manager's database had flagged the problems before Gary Neville even boarded the plane. Adam Charles asks the question nobody at Valencia was willing to: what if they'd just run the simulation first?`,
    pts:[[-2.24,53.48,'Manchester'],[-0.38,39.47,'Valencia, Spain']], arcs:[[0,1]] },
  { i:2, c:'#2db87a', tc:'t2', il:'002 · Business',
    title:'The Red Bull Group: Thinking Outside the Box', sub:'Salzburg → Leipzig → New York → Brazil',
    writer:'David Whitworth',
    text:`They acquired Salzburg. Built Leipzig from the fifth division. Launched in New York. Planted a flag in Brazil. David Whitworth follows the Red Bull empire across three continents and finds a model that shouldn't work — but keeps on winning.`,
    pts:[[13.04,47.8,'Salzburg'],[12.38,51.33,'Leipzig'],[-74.01,40.71,'New York'],[-46.54,-22.95,'Bragança, Brazil']], arcs:[[0,1],[0,2],[0,3]] },
  { i:2, c:'#2db87a', tc:'t2', il:'002 · Business',
    title:'Asian Monikers & Club Naming', sub:'Shanghai · Guangzhou · Seoul — Corporate Identity Crisis',
    writer:'Deji Awoyemi',
    text:`Ulsan Hyundai. Suwon Samsung. Guangzhou Evergrande. Deji Awoyemi explores the clubs whose names told you exactly who owned them — until one day, the Chinese government said they didn't anymore. Jiangsu Suning chose to dissolve instead.`,
    pts:[[121.47,31.23,'Shanghai'],[113.26,23.13,'Guangzhou'],[126.98,37.57,'Seoul']], arcs:[[0,1],[1,2]] },
  { i:2, c:'#2db87a', tc:'t2', il:'002 · Business',
    title:'Players: Boot & Sponsorship Deals', sub:'London · Paris · Lisbon — The New Sponsor Game',
    writer:'Deji Awoyemi',
    text:`Pogba and Stormzy. Neymar leaving Nike for Puma. Ronaldo's CR7 brand. Deji Awoyemi maps the moment footballers stopped being sponsored and started building empires — and asks who's really in charge of the deal.`,
    pts:[[-0.12,51.5,'London'],[2.35,48.85,'Paris'],[-9.14,38.71,'Lisbon']], arcs:[[0,1],[1,2]] },
  { i:2, c:'#2db87a', tc:'t2', il:'002 · Business',
    title:'Clubs: Rebranding — The Case of Juventus', sub:'Turin → Global Brand',
    writer:'Deji Awoyemi',
    text:`They stripped the stripes from their crest and replaced a century of identity with a minimalist J. Deji Awoyemi follows Juventus's rebrand from Turin to the world — and finds a club that signed Ronaldo not just for goals, but for global reach.`,
    pts:[[7.68,45.07,'Turin'],[-3.7,40.42,'Madrid'],[-0.12,51.5,'London']], arcs:[[0,1],[0,2]] },

  // ── 003 · MOTHERLAND ────────────────────────────────────────────────────
  { i:3, c:'#f0a030', tc:'t3', il:'003 · Motherland',
    title:'Finding Home Away From Home', sub:'England · Jamaica · Nigeria — Sporting Nationality',
    writer:'Deji Awoyemi',
    text:'Ravel Morrison spent six years in the system before Jamaica finally felt like home. Deji Awoyemi asks the question that sits at the heart of every diaspora story in sport: what do you owe the country that shaped you — and the one that made you?',
    pts:[[-1.17,52.36,'England'],[-77.29,18.11,'Jamaica'],[8.68,9.08,'Nigeria']], arcs:[[0,1],[0,2]] },
  { i:3, c:'#f0a030', tc:'t3', il:'003 · Motherland',
    title:'Commander-in-Chiefs: Kaizer Chiefs', sub:'Soweto → Atlanta, USA → Johannesburg',
    writer:'Shivam Pathak',
    text:'Kaizer Motaung was born in Soweto, played for Orlando Pirates, and was lured to Atlanta in the North American Soccer League. Shivam Pathak follows the journey home — and the power struggle that ended with one man founding the most supported club in South Africa.',
    pts:[[27.86,-26.27,'Soweto'],[-84.39,33.75,'Atlanta Chiefs, USA'],[28.04,-26.2,'Johannesburg']], arcs:[[0,1],[1,2]] },
  { i:3, c:'#f0a030', tc:'t3', il:'003 · Motherland',
    title:'The Other African Cup of Nations (CHAN)', sub:'DR Congo · Morocco · Cameroon',
    writer:'Deji Awoyemi',
    text:'Same trophy. Different rules. Only players still playing at home can enter. Deji Awoyemi explores the African Nations Championship — the tournament that rewards those who never left — and finds a different kind of pride in every match.',
    pts:[[21.76,-4.0,'DR Congo'],[-7.09,31.79,'Morocco'],[11.5,3.85,'Cameroon']], arcs:[[0,2],[1,2]] },
  { i:3, c:'#f0a030', tc:'t3', il:'003 · Motherland',
    title:'Barbados 4–2 Grenada', sub:'Barbados ↔ Grenada — Caribbean',
    writer:'Peter Kenny Jones',
    text:'Barbados needed to win by two goals. With seven minutes left, trailing 2–1, they did something football had never seen before. Peter Kenny Jones revisits the most chaotic match in Caribbean history — a game where both sides were trying to score own goals.',
    pts:[[-59.54,13.19,'Barbados'],[-61.68,12.12,'Grenada']], arcs:[[0,1]] },
  { i:3, c:'#f0a030', tc:'t3', il:'003 · Motherland',
    title:'Fans Are Everywhere (The Numbers)', sub:'Nigeria · Ghana · Jamaica → Premier League',
    writer:'Shivam Pathak',
    text:`Shivam Pathak maps the data behind the Premier League's global fanbase — from Lagos to Kingston, from Accra to London. The numbers tell a story about where the game is watched, who watches it, and who the sport still hasn't properly acknowledged.`,
    pts:[[8.68,9.08,'Nigeria'],[-.2,5.56,'Ghana'],[-77.29,18.11,'Jamaica'],[-0.12,51.5,'London']], arcs:[[0,3],[1,3],[2,3]] },
  { i:3, c:'#f0a030', tc:'t3', il:'003 · Motherland',
    title:'Jay-Jay Okocha', sub:'Enugu, Nigeria → Paris Saint-Germain → Bolton',
    writer:'Nadim Bart-Williams',
    text:`So good they named him twice. Nadim Bart-Williams traces Jay-Jay Okocha's journey from Enugu to the Reebok Stadium via Paris — and finds a player so extraordinary that even the greatest footballers in the world would stop to watch him warm up.`,
    pts:[[7.5,6.44,'Enugu, Nigeria'],[2.35,48.85,'Paris'],[-2.43,53.58,'Bolton']], arcs:[[0,1],[1,2]] },
  { i:3, c:'#f0a030', tc:'t3', il:'003 · Motherland',
    title:"Africa's Turbulent Path to the World Cup", sub:'Senegal · Morocco · Ghana → Qatar 2022',
    writer:'Adam Khan',
    text:`Fifty-four nations. Five places. Adam Khan follows Africa's qualification campaigns through the chaos, the heartbreak, and the injustice — all the way to Morocco's semi-final in Qatar, where a continent held its breath and finally exhaled.`,
    pts:[[-14.45,14.5,'Senegal'],[-7.09,31.79,'Morocco'],[-1.02,7.95,'Ghana'],[51.52,25.28,'Qatar']], arcs:[[0,3],[1,3],[2,3]] },
  { i:3, c:'#f0a030', tc:'t3', il:'003 · Motherland',
    title:'AFCON 2021: The Sideline Stories', sub:'Cameroon (host) · Senegal · Egypt',
    writer:'Charlie Hutton',
    text:`The host nation's near-miss. Salah's haunted face in the final. Mané's penalty that made an entire continent cry. Charlie Hutton goes beyond the results and finds the AFCON nobody televised — the stories that happened in the margins.`,
    pts:[[11.5,3.85,'Cameroon'],[-14.45,14.5,'Senegal'],[30.8,26.82,'Egypt']], arcs:[[0,1],[0,2]] },
  { i:3, c:'#f0a030', tc:'t3', il:'003 · Motherland',
    title:"How 'Little Spain' Conquered Africa?", sub:'Malabo, Equatorial Guinea ↔ Spain',
    writer:'Daniel Noruwa',
    text:`A squad built largely in Spain. A nation smaller than most Spanish provinces. Daniel Noruwa pulls at the thread of Equatorial Guinea's AFCON runs — and finds a story about heritage, colonial ties, and what it means to represent a place you've barely visited.`,
    pts:[[8.78,3.75,'Malabo, Equatorial Guinea'],[-3.74,40.46,'Spain']], arcs:[[0,1]] },
  { i:3, c:'#f0a030', tc:'t3', il:'003 · Motherland',
    title:'Julian Wade — Dominican All-Time', sub:'Roseau, Dominica → Montserrat → Brechin City, Scotland',
    writer:'Gary Cassidy',
    text:`Dominica's all-time top scorer was working as a police officer in Montserrat when Gary Cassidy found him. Wade had never played professional football outside the Caribbean. Brechin City were about to change that — and the reason he said yes had nothing to do with football.`,
    pts:[[-61.39,15.3,'Roseau, Dominica'],[-62.19,16.74,'Montserrat'],[-2.66,56.73,'Brechin City, Scotland']], arcs:[[0,1],[1,2]] },
  { i:3, c:'#f0a030', tc:'t3', il:'003 · Motherland',
    title:'Montserrat — Football at the Edge of the World', sub:'Montserrat → London (diaspora)',
    writer:'Charlie Hutton',
    text:'A volcanic eruption in 1997 buried half the island. Most Montserratians now live abroad — many in London. Charlie Hutton visits the national team and finds a group of players representing a home that most of them can only remember.',
    pts:[[-62.19,16.74,'Montserrat'],[-0.12,51.5,'London']], arcs:[[0,1]] },
  { i:3, c:'#f0a030', tc:'t3', il:'003 · Motherland',
    title:'Reggae Girlz on the Rise', sub:`Jamaica → France Women's World Cup 2019`,
    writer:'Gabrielle De Cordova-Harris',
    text:'Twice disbanded. Six years without a team. Then Cedella Marley walked in and changed everything. Gabrielle De Cordova-Harris tells the story of the Reggae Girlz — and the Caribbean island that made World Cup history.',
    pts:[[-77.29,18.11,'Jamaica'],[2.21,46.23,'France']], arcs:[[0,1]] },
  { i:3, c:'#f0a030', tc:'t3', il:'003 · Motherland',
    title:'Fans Are Everywhere (Capturing the Obsession)', sub:'Koulikoro, Mali → France',
    writer:'Émile-Samory Fofana',
    text:`French-Malian photographer Émile-Samory Fofana took his camera to Koulikoro and found the Champions League playing out in red dust and torn nets. His photographs don't explain African football — they make you feel it.`,
    pts:[[-7.56,12.86,'Koulikoro, Mali'],[2.21,46.23,'France']], arcs:[[0,1]] },

  // ── 004 · HERITAGE ──────────────────────────────────────────────────────
  { i:4, c:'#d4537e', tc:'t4', il:'004 · Heritage',
    title:'Dinamo Zagreb: The Unheralded Talent Factory', sub:'Zagreb → Madrid · Manchester · Leipzig',
    writer:'Adam Khan',
    text:`Fourteen of Croatia's twenty-three World Cup finalists came through Dinamo Zagreb. Adam Khan visits the academy that only recruits within forty-five minutes' travel and discovers a philosophy so simple it makes the rest of European football look complicated.`,
    pts:[[15.98,45.81,'Zagreb'],[-3.7,40.42,'Madrid'],[-2.24,53.48,'Manchester'],[12.38,51.33,'Leipzig']], arcs:[[0,1],[0,2],[0,3]] },
  { i:4, c:'#d4537e', tc:'t4', il:'004 · Heritage',
    title:'Faith, Belief and Superstition', sub:'Lagos, Nigeria → Lisbon, Portugal → Manchester',
    writer:'Olakunle Afolabi',
    text:'TB Joshua predicted three Super Eagles results before they happened. Béla Guttmann cursed Benfica in 1962. Olakunle Afolabi follows the hex, the prophecy and the prayer — and finds that in football, the line between faith and coincidence has always been thin.',
    pts:[[3.38,6.52,'Lagos, Nigeria'],[-9.14,38.72,'Lisbon (Benfica)'],[-2.24,53.48,'Manchester']], arcs:[[0,2],[1,2]] },
  { i:4, c:'#d4537e', tc:'t4', il:'004 · Heritage',
    title:'Finding Home Away From Home (003 Archive)', sub:'England · Jamaica · Liberia · USA',
    writer:'Deji Awoyemi',
    text:`From Ravel Morrison's six-year wait to become a Reggae Boy, to a Liberian sprinter who'd never left the United States before Tokyo 2021 — Deji Awoyemi asks what it actually costs to choose a different flag.`,
    pts:[[-1.17,52.36,'England'],[-77.29,18.11,'Jamaica'],[-9.43,6.43,'Liberia'],[-97.0,38.9,'USA']], arcs:[[0,1],[2,3]] },
  { i:4, c:'#d4537e', tc:'t4', il:'004 · Heritage',
    title:'The Language of Football', sub:`London · Rio de Janeiro · Milan — Football's Global Tongue`,
    writer:'Aji Thompson',
    text:`"Park the bus." "Gegenpressing." "Ginga." "Catenaccio." Aji Thompson maps football's own language — from Mourinho's press conferences to Brazilian terraces to a UK rapper dropping tactical jargon into a track about survival.`,
    pts:[[-0.12,51.5,'London'],[-43.17,-22.91,'Rio de Janeiro'],[9.19,45.46,'Milan']], arcs:[[0,1],[0,2]] },
  { i:4, c:'#d4537e', tc:'t4', il:'004 · Heritage',
    title:'The Beautiful Game in Harmony', sub:'Ghana → London → New York',
    writer:'Theresa R. Fianko',
    text:`Theresa R. Fianko begins in Ghana, where Jama cheer songs are passed down like heirlooms — and ends with Jay-Z's Roc Nation signing footballers like record artists. Somewhere between Accra and the Yankee Stadium, music and football stopped being separate things.`,
    pts:[[-1.02,7.95,'Ghana'],[-0.12,51.5,'London'],[-74.01,40.71,'New York']], arcs:[[0,1],[1,2]] },
  { i:4, c:'#d4537e', tc:'t4', il:'004 · Heritage',
    title:'Football: Lyrics for Lyrics... Calm', sub:'London · Paris · Lagos',
    writer:'Nathan Mutuale',
    text:`Burna Boy at the Champions League final. Mbappé at Lil Baby's show. Rashford in the studio with Dave. Nathan Mutuale finds the moment pop culture and the pitch became the same stage — and asks whether the merger has changed both forever.`,
    pts:[[-0.12,51.5,'London'],[2.35,48.85,'Paris'],[3.38,6.52,'Lagos']], arcs:[[0,1],[2,1]] },
  { i:4, c:'#d4537e', tc:'t4', il:'004 · Heritage',
    title:'The Revolution Will Be Televised', sub:'New York (CBS) → London',
    writer:'Aji Thompson',
    text:'Thierry Henry, Carragher and Micah Richards walked into a studio in New York and accidentally invented a new kind of football television. "Do you like Brest?" got fifteen million views in forty-eight hours. Aji Thompson asks: what took everyone so long?',
    pts:[[-74.01,40.71,'New York'],[-0.12,51.5,'London']], arcs:[[0,1]] },
  { i:4, c:'#d4537e', tc:'t4', il:'004 · Heritage',
    title:'Is Social Media Shaping the New Era of Fandom?', sub:'Tehran, Iran → Manchester → New York',
    writer:'Shailja Bahety',
    text:`An Iranian fan cried outside Ronaldo's hotel in Tehran and woke up famous. Manchester City built a stadium in the metaverse. Shailja Bahety follows fandom to places no one expected it to go — and finds it's already moved on from there.`,
    pts:[[51.39,35.69,'Tehran, Iran'],[-2.24,53.48,'Manchester'],[-74.01,40.71,'New York']], arcs:[[0,1],[2,1]] },
  { i:4, c:'#d4537e', tc:'t4', il:'004 · Heritage',
    title:'The Power of the Voice — Fan Ownership', sub:'Ebbsfleet, Kent · Crawley, Sussex → London',
    writer:'Aaron Jaffe',
    text:'Thirty-five pounds per share. MyFootballClub bought Ebbsfleet United, reached Wembley — then collapsed. Aaron Jaffe follows the idealism, the chaos, and the crypto cowboys who nearly sent Crawley Town into the National League.',
    pts:[[0.32,51.44,'Ebbsfleet, Kent'],[-0.19,51.11,'Crawley, Sussex'],[-0.12,51.5,'London']], arcs:[[0,2],[1,2]] },
  { i:4, c:'#d4537e', tc:'t4', il:'004 · Heritage',
    title:'The Stats Revolution', sub:'England — The Data vs The Eye Test',
    writer:'Neel Shelat',
    text:'"Enough of this statistical rubbish — I watched the lad play." Neil Warnock said it. Neel Shelat asks whether he had a point — and finds that in the gap between xG and instinct lies everything interesting about modern football.',
    pts:[[-0.12,51.5,'London'],[-2.24,53.48,'Manchester']], arcs:[[0,1]] },
  { i:4, c:'#d4537e', tc:'t4', il:'004 · Heritage',
    title:'Empiricism, Rationalism & Match-of-the-Day-ism', sub:'England — Three Ways of Watching Football',
    writer:'Tristan Scott',
    text:'Three philosophers walk into a stadium. Tristan Scott gives them each a seat and listens to what they say at full time — and discovers that how you watch football reveals more about you than the scoreline ever could.',
    pts:[[-0.12,51.5,'London'],[-2.24,53.48,'Manchester']], arcs:[[0,1]] },
  { i:4, c:'#d4537e', tc:'t4', il:'004 · Heritage',
    title:'The Community of Football', sub:'Beijing → Manchester — Who Owns the Club?',
    writer:'James Turell',
    text:'A fan in Beijing saves for years to spend £2,500 on one game at Old Trafford. A Mancunian has had the same seat in the Stretford End since before that fan was born. James Turell sits them both down and asks: who has the stronger claim?',
    pts:[[116.4,39.9,'Beijing'],[-2.24,53.48,'Manchester']], arcs:[[0,1]] },
  { i:4, c:'#d4537e', tc:'t4', il:'004 · Heritage',
    title:'Beyond the Pitch — Stadiums as Arenas', sub:'Madrid · Milan · Tottenham',
    writer:'Charlie Baker',
    text:'Real Madrid spent over a billion euros on a building. Tottenham Hotspur Stadium hosted Beyoncé. Inter and AC Milan both separately hired the same architect. Charlie Baker asks: at what point does the stadium become the product — and the football just the excuse?',
    pts:[[-3.7,40.42,'Madrid'],[9.19,45.46,'Milan'],[-0.07,51.6,'Tottenham']], arcs:[[0,1],[1,2],[0,2]] },
  { i:4, c:'#d4537e', tc:'t4', il:'004 · Heritage',
    title:'Pawns and Kings', sub:'Johannesburg · Senegal · Nigeria',
    writer:'Tom Watkins',
    text:'Siphiwe Tshabalala. Soccer City. Eighty-four thousand people. Tom Watkins revisits the 2010 World Cup through the lens of who the game made into kings — and who it was still using as pawns.',
    pts:[[28.04,-26.2,'Soccer City, Johannesburg'],[-14.45,14.5,'Senegal'],[8.68,9.08,'Nigeria']], arcs:[[0,1],[0,2]] },
  { i:4, c:'#d4537e', tc:'t4', il:'004 · Heritage',
    title:"How Labels Are 'Kyl-ing' Your View of Modern Football", sub:'Paris · London · Madrid',
    writer:'Joseph Taylor',
    text:`"A work of art does not exist outside the perception of the audience." Joseph Taylor takes the labels we stick on footballers — "the next Pelé," "the new Mbappé" — and asks whether we've ever actually seen a player clearly, or only the idea of them.`,
    pts:[[2.35,48.85,'Paris'],[-0.12,51.5,'London'],[-3.7,40.42,'Madrid']], arcs:[[0,2],[1,2]] },
  { i:4, c:'#d4537e', tc:'t4', il:'004 · Heritage',
    title:'How Media Influenced Sports — Captain Tsubasa', sub:'Tokyo → France · Spain · Italy',
    writer:'Aji Thompson',
    text:`A 1983 manga about a boy with a dream turned an entire baseball nation into football obsessives. Aji Thompson traces Tsubasa's influence through the testimonies of Zidane, Del Piero, Iniesta and Mbappé — and asks whether a story can be more powerful than a coach.`,
    pts:[[139.69,35.68,'Tokyo'],[2.21,46.23,'France'],[-3.74,40.46,'Spain'],[12.48,41.9,'Rome, Italy']], arcs:[[0,1],[0,2],[0,3]] },
  { i:4, c:'#d4537e', tc:'t4', il:'004 · Heritage',
    title:'The Influence of Football in Fashion — and Vice Versa', sub:'London · Paris · Lisbon',
    writer:'Sharufa Yuma',
    text:`From Stone Island on the terraces to Corteiz on Vinicius Jr, from Martine Rose on the runway to Louis Vuitton x Portugal — Sharufa Yuma follows the stitch that connects football culture to fashion culture, and finds they've been the same thing for decades.`,
    pts:[[-0.12,51.5,'London'],[2.35,48.85,'Paris'],[-9.14,38.71,'Lisbon']], arcs:[[0,1],[1,2]] },
  { i:4, c:'#d4537e', tc:'t4', il:'004 · Heritage',
    title:"Africa's Turbulent Path to the World Cup (002 Archive)", sub:'Morocco · Senegal → Qatar 2022',
    writer:'Adam Khan',
    text:'Morocco reached the semi-final. The first African nation ever. Adam Khan revisits the archive piece from Issue 002 that predicted none of this — and asks what it means for a continent that was told, for decades, to be patient.',
    pts:[[-7.09,31.79,'Morocco'],[-14.45,14.5,'Senegal'],[51.52,25.28,'Qatar']], arcs:[[0,2],[1,2]] },
  { i:4, c:'#d4537e', tc:'t4', il:'004 · Heritage',
    title:'Changing the Culture of Football', sub:'Manchester · London · Senegal',
    writer:'Muqqaddam Malik',
    text:`Rashford and free school meals. Sterling's Instagram post. Mané building hospitals back home. Muqqaddam Malik finds the moment footballers stopped being content with playing the game — and started trying to change the world around it.`,
    pts:[[-2.24,53.48,'Manchester'],[-0.12,51.5,'London'],[-14.45,14.5,'Senegal']], arcs:[[0,1],[1,2]] },
];

// ── CLUSTERING ────────────────────────────────────────────────────
// Group all unique geographic points across all stories.
// At render time we cluster in screen-space: if two points project
// within CLUSTER_RADIUS pixels of each other, they merge into one
// cluster dot showing a count badge. Click the cluster to expand it.

// Collect every unique [lon, lat] point across all stories with back-refs
function getAllPoints() {
  const pts = [];
  STORIES.forEach(story => {
    story.pts.forEach(pt => {
      pts.push({ lon: pt[0], lat: pt[1], label: pt[2] || '', story });
    });
  });
  return pts;
}
const ALL_POINTS = getAllPoints();

// Screen-space cluster radius in CSS pixels (before DPR scaling)
const CLUSTER_RADIUS = 22; // tighter clusters = more individual dots visible when zoomed in

function computeClusters(proj, rot, activeFilter) {
  // Only consider visible points that pass the active filter
  const visible = ALL_POINTS.filter(p =>
    isVisible(p.lon, p.lat, rot) &&
    (activeFilter === null || p.story.i === activeFilter)
  );

  const clusters = [];
  const assigned = new Set();

  visible.forEach((p, i) => {
    if (assigned.has(i)) return;
    const xy = proj([p.lon, p.lat]);
    if (!xy) return;

    // Find all visible points within CLUSTER_RADIUS of this one
    const members = [p];
    assigned.add(i);

    visible.forEach((q, j) => {
      if (assigned.has(j)) return;
      const qxy = proj([q.lon, q.lat]);
      if (!qxy) return;
      if (Math.hypot(xy[0] - qxy[0], xy[1] - qxy[1]) < CLUSTER_RADIUS) {
        members.push(q);
        assigned.add(j);
      }
    });

    // Centroid in screen space
    let sx = 0, sy = 0;
    members.forEach(m => {
      const mxy = proj([m.lon, m.lat]);
      if (mxy) { sx += mxy[0]; sy += mxy[1]; }
    });
    sx /= members.length; sy /= members.length;

    // Dominant issue colour (most common issue number among members' stories)
    const counts = {};
    members.forEach(m => {
      counts[m.story.i] = (counts[m.story.i] || 0) + 1;
    });
    const dominantIssue = parseInt(Object.entries(counts).sort((a,b) => b[1]-a[1])[0][0]);
    const COLS = { 1:'#4a90d9', 2:'#2db87a', 3:'#f0a030', 4:'#d4537e' };
    const colour = COLS[dominantIssue] || '#fff';

    // Unique stories in this cluster
    const storySet = [...new Set(members.map(m => m.story))];

    clusters.push({ x: sx, y: sy, members, stories: storySet, colour, count: storySet.length });
  });

  return clusters;
}

export default function DottedGlobe({ height = 660 }) {
  const canvasRef = useRef(null);
  const stateRef = useRef({
    rot: [-10, -30],
    dragging: false,
    lastXY: null,
    vel: [0, 0],
    hov: null,
    worldGeo: null,
    proj: null,
    pathGen: null,
    rafId: null,
    // Zoom
    scale: null,
    scaleMin: null,
    scaleMax: null,
    pinchDist: null,
    // Clustering
    expandedCluster: null, // cluster object currently expanded
    // Interaction states
    locked: null,     // story clicked — drives panel + arc glow
    hovCluster: null, // cluster under cursor
  });
  const [panel, setPanel] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Mobile detection
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Sync activeFilter to stateRef so canvas render can read it
  // Clear locked story if it doesn't match the new filter
  useEffect(() => {
    const st = stateRef.current;
    if (!st) return;
    st.activeFilter = activeFilter;
    if (activeFilter !== null && st.locked && st.locked.i !== activeFilter) {
      st.locked = null;
      setPanel(null);
    }
    st.render?.();
  }, [activeFilter]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Use actual container width — fall back to window if offsetWidth is 0 at mount
    const getW = () => canvas.parentElement?.offsetWidth || canvas.offsetWidth || window.innerWidth;
    const W = getW();
    const H = height;
    const DPR = devicePixelRatio || 1;
    canvas.width = W * DPR;
    canvas.height = H * DPR;
    canvas.style.width = W + 'px';
    canvas.style.height = H + 'px';
    const ctx = canvas.getContext('2d');
    ctx.scale(DPR, DPR);

    const CX = W / 2, CY = H / 2;
    // On mobile, make the globe fill more of the screen
    const SCALE = Math.min(W, H) * (W < 768 ? 0.48 : 0.43);

    const proj = d3.geoOrthographic().scale(SCALE).translate([CX, CY]).clipAngle(90);
    const pathGen = d3.geoPath(proj, ctx);
    const graticule = d3.geoGraticule()();
    const st = stateRef.current;

    // Zoom bounds: 0.5× to 2.5× the default scale
    st.scale    = SCALE;
    st.scaleMin = SCALE * 0.5;
    st.scaleMax = SCALE * 2.5;

    // Pulse animation
    let pulseFrame = null;
    function animatePulse() {
      if (!st.hov && !st.hovCluster) { pulseFrame = null; return; }
      render();
      pulseFrame = requestAnimationFrame(animatePulse);
    }
    st.startPulse = () => { if (!pulseFrame) animatePulse(); };
    st.stopPulse  = () => { if (pulseFrame) { cancelAnimationFrame(pulseFrame); pulseFrame = null; } };
    st.proj = proj;
    st.pathGen = pathGen;

    function render() {
      const { rot, hov, worldGeo } = st;
      const locked = st.locked; // clicked story — drives arc glow
      proj.scale(st.scale).rotate([rot[0], rot[1], 0]);
      ctx.clearRect(0, 0, W, H);

      ctx.beginPath(); pathGen({ type: 'Sphere' });
      ctx.fillStyle = '#060f1a'; ctx.fill();

      if (worldGeo) {
        ctx.beginPath(); pathGen(worldGeo);
        ctx.fillStyle = '#122b18'; ctx.fill();
        ctx.strokeStyle = '#1a3820'; ctx.lineWidth = 0.3; ctx.stroke();
      }

      ctx.beginPath(); pathGen(graticule);
      ctx.strokeStyle = 'rgba(255,255,255,0.025)'; ctx.lineWidth = 0.5; ctx.stroke();

      // Atmosphere glow
      ctx.beginPath(); pathGen({ type: 'Sphere' });
      ctx.strokeStyle = 'rgba(80,140,255,0.1)'; ctx.lineWidth = 8; ctx.stroke();
      ctx.beginPath(); pathGen({ type: 'Sphere' });
      ctx.strokeStyle = 'rgba(255,255,255,0.06)'; ctx.lineWidth = 1; ctx.stroke();

      // Arcs — draw in two passes: dim base layer, then bright active layer
      const af = st.activeFilter;
      const visibleStories = st.expandedCluster ? st.expandedCluster.stories : STORIES;

      // Pass 1: all arcs
      visibleStories.forEach(s => {
        const isActive = af === null || s.i === af;
        const isHovered = s === hov;
        if (isHovered) return; // drawn in pass 2
        if (!isActive) return; // filtered out — completely hidden
        s.arcs.forEach(([ai, bi]) => {
          ctx.save();
          ctx.beginPath();
          pathGen({ type: 'LineString', coordinates: [s.pts[ai].slice(0, 2), s.pts[bi].slice(0, 2)] });
          ctx.strokeStyle = s.c;
          ctx.lineWidth = af !== null ? 1.0 : 0.8;
          ctx.setLineDash([4, 6]);
          ctx.globalAlpha = af !== null ? 0.5 : 0.2;
          ctx.stroke();
          ctx.restore();
        });
      });

      // Pass 2: active story arc — 3-layer glow (locked click takes priority over hover)
      // Respect filter: if locked story's issue is filtered out, treat as no selection
      const rawActive = locked || hov;
      const activeStory = rawActive && (af === null || rawActive.i === af) ? rawActive : null;
      if (activeStory) {
        const t = Date.now() / 1000;
        const pulse = 0.18 + 0.08 * Math.sin(t * 2.2);

        activeStory.arcs.forEach(([ai, bi]) => {
          const coords = [activeStory.pts[ai].slice(0, 2), activeStory.pts[bi].slice(0, 2)];
          const geo = { type: 'LineString', coordinates: coords };

          // Layer 1: wide outer glow
          ctx.save();
          ctx.beginPath(); pathGen(geo);
          ctx.strokeStyle = activeStory.c;
          ctx.lineWidth = 22;
          ctx.setLineDash([]);
          ctx.globalAlpha = pulse;
          ctx.stroke();
          ctx.restore();

          // Layer 2: mid glow
          ctx.save();
          ctx.beginPath(); pathGen(geo);
          ctx.strokeStyle = activeStory.c;
          ctx.lineWidth = 7;
          ctx.setLineDash([]);
          ctx.globalAlpha = 0.45;
          ctx.stroke();
          ctx.restore();

          // Layer 3: crisp core
          ctx.save();
          ctx.beginPath(); pathGen(geo);
          ctx.strokeStyle = '#fff';
          ctx.lineWidth = 0.5;
          ctx.setLineDash([]);
          ctx.globalAlpha = 0.6;
          ctx.stroke();
          ctx.restore();

          ctx.save();
          ctx.beginPath(); pathGen(geo);
          ctx.strokeStyle = activeStory.c;
          ctx.lineWidth = 1.8;
          ctx.setLineDash([]);
          ctx.globalAlpha = 1;
          ctx.stroke();
          ctx.restore();

          // Endpoint dots (visible side only)
          [activeStory.pts[ai], activeStory.pts[bi]].forEach((pt, idx) => {
            if (!isVisible(pt[0], pt[1], rot)) return;
            const xy = proj([pt[0], pt[1]]);
            if (!xy) return;
            const dotPulse = 4 + 1.5 * Math.sin(t * 2.2 + idx);
            ctx.beginPath(); ctx.arc(xy[0], xy[1], dotPulse + 5, 0, 2*Math.PI);
            ctx.fillStyle = activeStory.c + '20'; ctx.fill();
            ctx.beginPath(); ctx.arc(xy[0], xy[1], dotPulse, 0, 2*Math.PI);
            ctx.fillStyle = activeStory.c; ctx.globalAlpha = 0.95; ctx.fill();
            ctx.strokeStyle = '#fff'; ctx.lineWidth = 1.5; ctx.globalAlpha = 1; ctx.stroke();
          });
        });

        // ── ALL PLACE NAME LABELS for hovered story ───────────────────────
        // Collect unique points across all arcs
        const seenPts = new Set();
        const labelPts = [];
        activeStory.arcs.forEach(([ai, bi]) => {
          [hov.pts[ai], hov.pts[bi]].forEach(pt => {
            const key = pt[0] + ',' + pt[1];
            if (!seenPts.has(key) && pt[2]) { seenPts.add(key); labelPts.push(pt); }
          });
        });

        // Separate into visible (on-globe labels) and hidden (off-globe legend)
        const visibleLabels = [];
        const hiddenLabels  = [];
        labelPts.forEach(pt => {
          if (isVisible(pt[0], pt[1], rot)) visibleLabels.push(pt);
          else hiddenLabels.push(pt);
        });

        ctx.font = '500 11px system-ui, sans-serif';
        ctx.textAlign = 'center';

        function drawPill(px, py, name, colour) {
          const tw = ctx.measureText(name).width;
          const pad = 6, pillW = tw + pad*2, pillH = 18;
          // Clamp x so pill never clips off canvas edge
          const margin = 8;
          const clampedX = Math.max(pillW/2 + margin, Math.min(W - pillW/2 - margin, px));
          ctx.save();
          ctx.globalAlpha = 0.92;
          ctx.fillStyle = '#060f1a';
          ctx.beginPath();
          if (ctx.roundRect) ctx.roundRect(clampedX - pillW/2, py - pillH + 4, pillW, pillH, 4);
          else ctx.rect(clampedX - pillW/2, py - pillH + 4, pillW, pillH);
          ctx.fill();
          ctx.fillStyle = colour;
          if (ctx.roundRect) ctx.roundRect(clampedX - pillW/2, py - pillH + 4, 3, pillH, [4,0,0,4]);
          else ctx.rect(clampedX - pillW/2, py - pillH + 4, 3, pillH);
          ctx.fill();
          ctx.globalAlpha = 1;
          ctx.fillStyle = '#fff';
          ctx.fillText(name, clampedX + 1, py);
          ctx.restore();
        }

        // On-globe labels — float above each visible dot
        visibleLabels.forEach(pt => {
          const xy = proj([pt[0], pt[1]]);
          if (!xy) return;
          drawPill(xy[0], xy[1] - 18, pt[2], hov.c);
        });

        // Off-globe labels — row of pills along the bottom of the globe
        if (hiddenLabels.length > 0) {
          const globeBottom = CY + proj.scale();
          const startY = globeBottom - 32;
          const totalW = hiddenLabels.reduce((acc, pt) => {
            return acc + ctx.measureText(pt[2]).width + 24;
          }, 0);
          let startX = CX - totalW / 2;

          hiddenLabels.forEach(pt => {
            const tw = ctx.measureText(pt[2]).width;
            const pillW = tw + 12;
            const px = startX + pillW / 2;

            ctx.save();
            ctx.globalAlpha = 0.85;
            ctx.fillStyle = '#060f1a';
            ctx.beginPath();
            if (ctx.roundRect) ctx.roundRect(px - pillW/2, startY - 14, pillW, 18, 4);
            else ctx.rect(px - pillW/2, startY - 14, pillW, 18);
            ctx.fill();
            ctx.fillStyle = activeStory.c;
            if (ctx.roundRect) ctx.roundRect(px - pillW/2, startY - 14, 3, 18, [4,0,0,4]);
            else ctx.rect(px - pillW/2, startY - 14, 3, 18);
            ctx.fill();
            ctx.globalAlpha = 1;
            ctx.fillStyle = 'rgba(255,255,255,0.6)';
            ctx.font = '500 11px system-ui, sans-serif';
            ctx.textAlign = 'center';
            // Small "↙ behind" indicator
            ctx.fillText(pt[2], px + 1, startY);
            ctx.restore();

            startX += pillW + 6;
          });

          // Small label explaining they're on the other side
          ctx.save();
          ctx.font = '400 9px system-ui, sans-serif';
          ctx.fillStyle = 'rgba(255,255,255,0.25)';
          ctx.textAlign = 'center';
          ctx.fillText('· other side of globe ·', CX, startY + 14);
          ctx.restore();
        }
      }

      // Clusters + individual dots
      const clusters = computeClusters(proj, rot, af);
      st._clusters = clusters;

      clusters.forEach(cl => {
        const expanded = st.expandedCluster === cl;
        if (cl.count === 1 || expanded) {
          cl.members.forEach(m => {
            const xy = proj([m.lon, m.lat]);
            if (!xy) return;
            const isHov = m.story === hov || m.story === locked;
            // Filter: hide if this story's issue doesn't match active filter
            if (af !== null && m.story.i !== af) return;
            const isActive = true;
            const r = isHov ? 6 : 3;
            ctx.globalAlpha = 1;

            // Outer glow
            if (isActive) {
              const glowR = isHov
                ? r + 6 + 2 * Math.sin(Date.now() / 400) // pulsing on hover
                : r + 4;
              ctx.beginPath(); ctx.arc(xy[0], xy[1], glowR, 0, 2 * Math.PI);
              ctx.fillStyle = m.story.c + (isHov ? '35' : '12'); ctx.fill();
            }

            // Main dot
            ctx.beginPath(); ctx.arc(xy[0], xy[1], r, 0, 2 * Math.PI);
            ctx.fillStyle = m.story.c; ctx.fill();

            // White ring
            ctx.strokeStyle = isHov ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.35)';
            ctx.lineWidth = isHov ? 1.5 : 0.7;
            ctx.stroke();

            // Inner specular highlight
            if (isActive && r >= 3) {
              ctx.beginPath(); ctx.arc(xy[0] - r*0.28, xy[1] - r*0.28, r * 0.32, 0, 2 * Math.PI);
              ctx.fillStyle = 'rgba(255,255,255,0.3)'; ctx.fill();
            }
            ctx.globalAlpha = 1;

            // Label on hover — pill with story title
            if (isHov && m.label) {
              const label = m.label;
              ctx.font = '500 10px system-ui,sans-serif';
              const tw = ctx.measureText(label).width;
              const px = xy[0], py = xy[1] - r - 10;
              // Dark pill
              ctx.globalAlpha = 0.88;
              ctx.fillStyle = '#0a1929';
              ctx.beginPath();
              if (ctx.roundRect) ctx.roundRect(px - tw/2 - 6, py - 13, tw + 12, 17, 4);
              else ctx.rect(px - tw/2 - 6, py - 13, tw + 12, 17);
              ctx.fill();
              // Colour left bar
              ctx.fillStyle = m.story.c;
              ctx.fillRect(px - tw/2 - 6, py - 13, 3, 17);
              ctx.globalAlpha = 1;
              // Text
              ctx.fillStyle = '#fff';
              ctx.textAlign = 'center';
              ctx.fillText(label, px, py);
            }
          });
          // Close button when expanded
          if (expanded) {
            ctx.font = '500 9px system-ui,sans-serif';
            ctx.fillStyle = 'rgba(255,255,255,0.4)';
            ctx.textAlign = 'center';
            ctx.fillText('tap to collapse', cl.x, cl.y - 22);
          }
        } else {
          const r = 14;
          const isHov = st.hovCluster === cl;
          const clFiltered = af !== null && !cl.stories.some(s => s.i === af);
          if (clFiltered) return; // filtered cluster — completely hidden
          ctx.globalAlpha = 1;
          // Draw cluster as overlapping coloured dots — filter by active issue
          const COLS = {1:'#4a90d9', 2:'#2db87a', 3:'#f0a030', 4:'#d4537e'};
          // Only show dots for issues that pass the current filter
          const uniqueIssues = [...new Set(cl.stories
            .filter(s => af === null || s.i === af)
            .map(s => s.i))];
          if (uniqueIssues.length === 0) return; // no matching stories — hide entirely
          const dotR = isHov ? 5 : 4;
          const spread = dotR * 1.4;
          const total = Math.min(uniqueIssues.length, 4);
          const offsetX = -(total - 1) * spread / 2;
          // Glow ring
          ctx.beginPath(); ctx.arc(cl.x, cl.y, dotR + 8, 0, 2 * Math.PI);
          ctx.fillStyle = (COLS[uniqueIssues[0]] || cl.colour) + '18'; ctx.fill();
          // Draw one dot per unique active issue colour
          uniqueIssues.slice(0, 4).forEach((issueNum, idx) => {
            const dx = offsetX + idx * spread;
            ctx.beginPath(); ctx.arc(cl.x + dx, cl.y, dotR, 0, 2 * Math.PI);
            ctx.fillStyle = COLS[issueNum] || cl.colour;
            ctx.fill();
            ctx.strokeStyle = 'rgba(255,255,255,0.7)'; ctx.lineWidth = 1.2; ctx.stroke();
          });
          ctx.globalAlpha = 1;
        }
      });
    }

    st.render = render;

    function clusterAt(mx, my) {
      if (!st._clusters) return null;
      for (const cl of st._clusters) {
        if (cl.count <= 1) continue;
        if (Math.hypot(cl.x - mx, cl.y - my) < 20) return cl;
      }
      return null;
    }

    function hitAt(mx, my, isMobileTouch) {
      const { rot } = st;
      // Hit radius: larger on touch for finger accuracy
      const dotR  = isMobileTouch ? 28 : 14;
      const arcR  = isMobileTouch ? 22 : 10;

      // Test ALL stories — expanded cluster gets priority, but all are hittable
      const pool = st.expandedCluster
        ? st.expandedCluster.stories
        : STORIES;

      // Collect ALL hits then return the closest one
      let best = null, bestDist = Infinity;

      for (const s of pool) {
        // Dot hit test
        for (const pt of s.pts) {
          if (!isVisible(pt[0], pt[1], rot)) continue;
          const xy = proj([pt[0], pt[1]]);
          if (!xy) continue;
          const d = Math.hypot(xy[0] - mx, xy[1] - my);
          if (d < dotR && d < bestDist) { best = s; bestDist = d; }
        }
        // Arc hit test — sample along the geodesic
        for (const [ai, bi] of s.arcs) {
          const interp = d3.geoInterpolate(s.pts[ai].slice(0, 2), s.pts[bi].slice(0, 2));
          for (let k = 0; k <= 60; k++) {
            const p = interp(k / 60);
            if (!isVisible(p[0], p[1], rot)) continue;
            const xy = proj(p);
            if (!xy) continue;
            const d = Math.hypot(xy[0] - mx, xy[1] - my);
            if (d < arcR && d < bestDist) { best = s; bestDist = d; }
          }
        }
      }
      return best;
    }

    function onMouseMove(e) {
      if (st.dragging) return;
      const r = canvas.getBoundingClientRect();
      const mx = e.clientX - r.left, my = e.clientY - r.top;
      const cl = clusterAt(mx, my);
      const found = cl ? null : hitAt(mx, my, false);
      if (cl !== st.hovCluster || found !== st.hov) {
        st.hovCluster = cl;
        st.hov = found;
        // Show pointer if hovering something interactive
        canvas.style.cursor = (cl || found || st.locked) ? 'pointer' : 'grab';
        if (cl || found || st.locked) st.startPulse?.(); else st.stopPulse?.();
        render();
      }
    }

    function onClick(e) {
      const r = canvas.getBoundingClientRect();
      const mx = e.clientX - r.left, my = e.clientY - r.top;
      const cl = clusterAt(mx, my);
      if (cl) {
        st.expandedCluster = st.expandedCluster === cl ? null : cl;
        render();
        return;
      }
      const found = hitAt(mx, my, false);
      if (found) {
        // Toggle: click same story again to deselect
        if (st.locked === found) {
          st.locked = null;
          setPanel(null);
        } else {
          st.locked = found;
          setPanel(found);
          st.startPulse?.();
        }
        render();
        return;
      }
      // Click empty space — clear lock
      if (st.locked) {
        st.locked = null;
        setPanel(null);
        render();
        return;
      }
      if (st.expandedCluster) {
        st.expandedCluster = null;
        render();
      }
    }

    function onMouseDown(e) {
      st.dragging = true;
      st.lastXY = [e.clientX, e.clientY];
      st.vel = [0, 0];
      canvas.style.cursor = 'grabbing';
      cancelAnimationFrame(st.rafId);
    }

    function onMouseMoveGlobal(e) {
      if (!st.dragging || !st.lastXY) return;
      const dx = e.clientX - st.lastXY[0], dy = e.clientY - st.lastXY[1];
      st.rot[0] += dx * 0.3;
      st.rot[1] = Math.max(-85, Math.min(85, st.rot[1] - dy * 0.3));
      st.vel = [dx * 0.25, -dy * 0.25];
      st.lastXY = [e.clientX, e.clientY];
      render();
    }

    function onMouseUp() {
      if (!st.dragging) return;
      st.dragging = false;
      st.lastXY = null;
      canvas.style.cursor = 'grab';
      const v = [...st.vel];
      function step() {
        if (Math.abs(v[0]) < 0.01 && Math.abs(v[1]) < 0.01) return;
        st.rot[0] += v[0];
        st.rot[1] = Math.max(-85, Math.min(85, st.rot[1] + v[1]));
        v[0] *= 0.94; v[1] *= 0.94;
        render();
        st.rafId = requestAnimationFrame(step);
      }
      step();
    }

    function onTouchStart(e) {
      e.preventDefault();
      if (e.touches.length === 2) {
        st.dragging = false;
        st.pinchDist = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY
        );
        st.tapStart = null;
      } else {
        st.dragging = true;
        st.lastXY = [e.touches[0].clientX, e.touches[0].clientY];
        st.vel = [0, 0];
        st.touchMoved = false;
        // Record tap start for tap detection
        st.tapStart = { x: e.touches[0].clientX, y: e.touches[0].clientY, t: Date.now() };
        cancelAnimationFrame(st.rafId);
      }
    }

    function onTouchMove(e) {
      e.preventDefault();
      if (e.touches.length === 2) {
        // Pinch zoom
        const dist = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY
        );
        if (st.pinchDist != null) {
          const ratio = dist / st.pinchDist;
          st.scale = Math.max(st.scaleMin, Math.min(st.scaleMax, st.scale * ratio));
          render();
        }
        st.pinchDist = dist;
        return;
      }
      if (!st.dragging) return;
      const dx = e.touches[0].clientX - st.lastXY[0];
      const dy = e.touches[0].clientY - st.lastXY[1];
      // Mark as moved if finger travels more than 6px
      if (Math.hypot(dx, dy) > 6) st.touchMoved = true;
      st.rot[0] += dx * 0.3;
      st.rot[1] = Math.max(-85, Math.min(85, st.rot[1] - dy * 0.3));
      st.vel = [dx * 0.25, -dy * 0.25];
      st.lastXY = [e.touches[0].clientX, e.touches[0].clientY];
      render();
    }

    function onTouchEnd(e) {
      st.dragging = false;
      st.pinchDist = null;

      // Tap detection: finger didn't move much and lifted quickly
      if (st.tapStart && !st.touchMoved && (Date.now() - st.tapStart.t) < 300) {
        const r = canvas.getBoundingClientRect();
        const tx = st.tapStart.x - r.left;
        const ty = st.tapStart.y - r.top;

        // Check cluster first
        const cl = clusterAt(tx, ty);
        if (cl) {
          st.expandedCluster = st.expandedCluster === cl ? null : cl;
          render();
          st.tapStart = null;
          return;
        }

        // Hit test with large mobile radius
        const found = hitAt(tx, ty, true);
        if (found) {
          if (st.locked === found) {
            st.locked = null;
            setPanel(null);
          } else {
            st.locked = found;
            setPanel(found);
            st.startPulse?.();
          }
          render();
          st.tapStart = null;
          return;
        }

        // Tap on empty space — collapse expanded cluster
        if (st.expandedCluster) {
          st.expandedCluster = null;
          render();
        }
      }
      st.tapStart = null;
    }

    // Scroll-wheel zoom — smooth, non-blocking
    function onWheel(e) {
      e.preventDefault();
      // normalise across trackpad / mouse wheel
      const delta = e.deltaY * (e.deltaMode === 1 ? 20 : 1);
      const factor = 1 - delta * 0.001;
      st.scale = Math.max(st.scaleMin, Math.min(st.scaleMax, st.scale * factor));
      render();
    }

    canvas.addEventListener('mousemove', onMouseMove);
    canvas.addEventListener('click', onClick);
    canvas.addEventListener('mousedown', onMouseDown);
    canvas.addEventListener('touchstart', onTouchStart, { passive: false });
    canvas.addEventListener('touchmove', onTouchMove, { passive: false });
    canvas.addEventListener('touchend', onTouchEnd);
    canvas.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('mousemove', onMouseMoveGlobal);
    window.addEventListener('mouseup', onMouseUp);

    render();

    // Load world map
    import('topojson-client').then(({ feature }) => {
      fetch('https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json')
        .then(r => r.json())
        .then(world => {
          st.worldGeo = feature(world, world.objects.countries);
          render();
        });
    });

    // ResizeObserver — re-initialise if canvas was 0-width at mount (common on mobile)
    let initialised = W > 0;
    const ro = new ResizeObserver(entries => {
      const newW = entries[0]?.contentRect.width;
      if (newW && newW > 0 && !initialised) {
        initialised = true;
        canvas.width = newW * DPR;
        canvas.height = H * DPR;
        canvas.style.width = newW + 'px';
        canvas.style.height = H + 'px';
        ctx.scale(DPR, DPR);
        proj.scale(Math.min(newW, H) * (newW < 768 ? 0.46 : 0.43));
        proj.translate([newW / 2, H / 2]);
        st.scale = Math.min(newW, H) * (newW < 768 ? 0.46 : 0.43);
        st.scaleMin = st.scale * 0.5;
        st.scaleMax = st.scale * 2.5;
        render();
      }
    });
    ro.observe(canvas.parentElement || canvas);

    return () => {
      canvas.removeEventListener('mousemove', onMouseMove);
      canvas.removeEventListener('click', onClick);
      canvas.removeEventListener('mousedown', onMouseDown);
      canvas.removeEventListener('touchstart', onTouchStart);
      canvas.removeEventListener('touchmove', onTouchMove);
      canvas.removeEventListener('touchend', onTouchEnd);
      canvas.removeEventListener('wheel', onWheel);
      window.removeEventListener('mousemove', onMouseMoveGlobal);
      window.removeEventListener('mouseup', onMouseUp);
      cancelAnimationFrame(st.rafId);
      ro.disconnect();
    };
  }, [height]);

  const ISSUE_COLORS = { 1: '#4a90d9', 2: '#2db87a', 3: '#f0a030', 4: '#d4537e' };

    const ISSUES = [
    { i: 1, c: '#4a90d9', label: '001', sub: 'Black Excellence', count: 14 },
    { i: 2, c: '#2db87a', label: '002', sub: 'Business',         count: 16 },
    { i: 3, c: '#f0a030', label: '003', sub: 'Motherland',       count: 13 },
    { i: 4, c: '#d4537e', label: '004', sub: 'Heritage',         count: 19 },
  ];

  function FilterItem({ issue }) {
    const active = activeFilter === issue.i;
    const dimmed = activeFilter !== null && !active;
    return (
      <button
        onClick={() => setActiveFilter(active ? null : issue.i)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          width: '100%', background: 'none', border: 'none',
          padding: isMobile ? '10px 16px' : '10px 14px',
          cursor: 'pointer', borderRadius: 8, marginBottom: 4,
          opacity: dimmed ? 0.3 : 1,
          transition: 'opacity 0.2s, background 0.15s',
          background: active ? issue.c + '18' : 'transparent',
        }}
        onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
        onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
      >
        {/* Colour dot */}
        <div style={{
          width: active ? 10 : 8, height: active ? 10 : 8,
          borderRadius: '50%', background: issue.c, flexShrink: 0,
          boxShadow: active ? `0 0 8px ${issue.c}88` : 'none',
          transition: 'all 0.2s',
        }} />
        <div style={{ textAlign: 'left' }}>
          <div style={{
            fontSize: 11, fontWeight: 600, letterSpacing: '0.06em',
            color: active ? issue.c : 'rgba(255,255,255,0.7)',
            lineHeight: 1.2, transition: 'color 0.2s',
          }}>
            {issue.label}
          </div>
          <div style={{
            fontSize: 10, color: active ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.35)',
            marginTop: 1, transition: 'color 0.2s',
          }}>
            {issue.sub}
          </div>
        </div>
        {/* Active indicator */}
        {active && (
          <div style={{
            marginLeft: 'auto', fontSize: 9, letterSpacing: '0.08em',
            color: issue.c, textTransform: 'uppercase', fontWeight: 600,
          }}>on</div>
        )}
      </button>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%', height, background: 'transparent', overflow: 'hidden' }}>
      <canvas
        ref={canvasRef}
        style={{ display: 'block', width: '100%', height: '100%', cursor: 'grab' }}
      />

      {/* ── DESKTOP FILTER SIDEBAR ─────────────────────────────── */}
      {!isMobile && (
        <div style={{
          position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
          background: 'rgba(8,25,42,0.85)',
          border: '0.5px solid rgba(255,255,255,0.1)',
          borderRadius: 12, padding: '12px 6px',
          backdropFilter: 'blur(12px)',
          zIndex: 20, minWidth: 140,
          fontFamily: 'system-ui, sans-serif',
        }}>
          <div style={{
            fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.25)', padding: '0 14px', marginBottom: 10,
          }}>
            Filter by issue
          </div>
          {ISSUES.map(iss => <FilterItem key={iss.i} issue={iss} />)}
          {activeFilter !== null && (
            <button
              onClick={() => setActiveFilter(null)}
              style={{
                width: '100%', marginTop: 8, padding: '6px 0',
                background: 'none', border: '0.5px solid rgba(255,255,255,0.1)',
                borderRadius: 6, color: 'rgba(255,255,255,0.3)',
                fontSize: 10, cursor: 'pointer', letterSpacing: '0.06em',
                transition: 'color 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.7)'}
              onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.3)'}
            >
              Clear filter
            </button>
          )}
        </div>
      )}

      {/* ── MOBILE FILTER TOGGLE ───────────────────────────────── */}
      {isMobile && (
        <button
          onClick={() => setDrawerOpen(o => !o)}
          style={{
            position: 'absolute', bottom: 100, right: 16, zIndex: 30,
            background: 'rgba(8,25,42,0.9)',
            border: '0.5px solid rgba(255,255,255,0.15)',
            borderRadius: 20, padding: '8px 14px',
            display: 'flex', alignItems: 'center', gap: 7,
            cursor: 'pointer', backdropFilter: 'blur(12px)',
          }}
        >
          {/* 4 coloured dots */}
          <div style={{ display: 'flex', gap: 3 }}>
            {ISSUES.map(iss => (
              <div key={iss.i} style={{
                width: 6, height: 6, borderRadius: '50%', background: iss.c,
                opacity: activeFilter === null || activeFilter === iss.i ? 1 : 0.25,
              }} />
            ))}
          </div>
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', letterSpacing: '0.06em' }}>
            {activeFilter ? `Issue 00${activeFilter}` : 'Filter'}
          </span>
          <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.4)' }}>
            {drawerOpen ? '↓' : '↑'}
          </span>
        </button>
      )}

      {/* ── MOBILE FILTER DRAWER ───────────────────────────────── */}
      {isMobile && (
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 25,
          background: 'rgba(6,18,30,0.97)',
          borderTop: '0.5px solid rgba(255,255,255,0.1)',
          borderRadius: '16px 16px 0 0',
          padding: '16px 8px 32px',
          transform: drawerOpen ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.3s ease',
          fontFamily: 'system-ui, sans-serif',
        }}>
          {/* Drag handle */}
          <div style={{
            width: 36, height: 4, borderRadius: 2,
            background: 'rgba(255,255,255,0.15)',
            margin: '0 auto 16px',
          }} />
          <div style={{
            fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.25)', padding: '0 16px', marginBottom: 12,
          }}>
            Filter by issue
          </div>
          {ISSUES.map(iss => <FilterItem key={iss.i} issue={iss} />)}
          {activeFilter !== null && (
            <button
              onClick={() => { setActiveFilter(null); setDrawerOpen(false); }}
              style={{
                width: 'calc(100% - 32px)', margin: '12px 16px 0',
                padding: '10px 0', background: 'none',
                border: '0.5px solid rgba(255,255,255,0.1)',
                borderRadius: 8, color: 'rgba(255,255,255,0.4)',
                fontSize: 11, cursor: 'pointer', display: 'block',
              }}
            >
              Clear filter
            </button>
          )}
        </div>
      )}

      {/* Hint */}
      <div style={{
        position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)',
        fontSize: 10, color: 'rgba(255,255,255,0.2)', pointerEvents: 'none',
        whiteSpace: 'nowrap', fontFamily: 'system-ui, sans-serif',
      }}>
        {isMobile
          ? 'Drag · Pinch to zoom · Tap to read'
          : 'Drag to rotate · Scroll to zoom · Click clusters to expand · Click dots to read'}
      </div>

      {/* Zoom buttons */}
      <div style={{
        position: 'absolute', bottom: isMobile ? 155 : 36, right: 16,
        display: 'flex', flexDirection: 'column', gap: 6, zIndex: 10,
      }}>
        {['+', '−'].map((label, i) => (
          <button
            key={label}
            onClick={() => {
              const st = stateRef.current;
              const factor = i === 0 ? 1.2 : 0.8;
              st.scale = Math.max(st.scaleMin, Math.min(st.scaleMax, st.scale * factor));
              st.render?.();
            }}
            style={{
              width: 32, height: 32, borderRadius: 6,
              background: 'rgba(255,255,255,0.08)',
              border: '0.5px solid rgba(255,255,255,0.15)',
              color: 'rgba(255,255,255,0.6)',
              fontSize: 18, lineHeight: 1,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Story panel — right side desktop, bottom sheet mobile */}
      {panel && (
        <div style={{
          position: 'absolute',
          ...(isMobile
            ? { bottom: 0, left: 0, right: 0, borderRadius: '16px 16px 0 0', padding: '16px 16px 32px', maxHeight: '45%' }
            : { top: 68, right: 16, width: 248, borderRadius: 12, padding: 14, maxHeight: 'calc(100% - 84px)' }
          ),
          background: '#0a1929',
          border: '0.5px solid rgba(255,255,255,0.12)',
          overflowY: 'auto', zIndex: 30,
          fontFamily: 'system-ui, sans-serif',
          boxShadow: '0 -4px 40px rgba(0,0,0,0.5)',
        }}>
          {isMobile && (
            <div style={{ width: 36, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.15)', margin: '0 auto 16px' }} />
          )}
          <button
            onClick={() => { setPanel(null); if (stateRef.current) { stateRef.current.locked = null; stateRef.current.render?.(); } }}
            style={{ position: 'absolute', top: isMobile ? 16 : 9, right: 14, background: 'none', border: 'none', fontSize: 17, cursor: 'pointer', color: '#888', lineHeight: 1 }}
          >×</button>
          <div style={{
            display: 'inline-block', fontSize: 10, fontWeight: 600, letterSpacing: '0.07em',
            textTransform: 'uppercase', padding: '2px 8px', borderRadius: 20, marginBottom: 8,
            background: ISSUE_COLORS[panel.i] + '22', color: ISSUE_COLORS[panel.i],
          }}>
            {panel.il}
          </div>
          <div style={{ fontSize: 14, fontWeight: 500, color: '#eee', marginBottom: 3, lineHeight: 1.3 }}>{panel.title}</div>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>{panel.sub}</div>
          {panel.writer && (
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginBottom: 10, letterSpacing: '0.04em' }}>
              Words by {panel.writer}
            </div>
          )}
          <div style={{ fontSize: 12, color: '#ccc', lineHeight: 1.65, borderLeft: '2px solid #4a90d9', paddingLeft: 9, fontStyle: 'italic' }}>
            {panel.text}
          </div>
        </div>
      )}
    </div>
  );
}
